import type {ReactNode, KeyboardEvent} from 'react'
import type {LucideIcon} from 'lucide-react'
import {useNavigate} from 'react-router-dom'
import {Card, CardContent} from '@/components/ui/card'
import {MutedText} from '@/components/shared/MutedText'
import {StatValue} from '@/components/shared/StatValue'
import {cn} from '@/lib/utils'

interface StatCardProps {
    label: string
    value: string | number
    variant?: 'default' | 'success' | 'danger' | 'primary' | 'muted'
    icon?: LucideIcon
    to?: string
    onClick?: () => void
    children?: ReactNode
    'data-testid'?: string
}

export function StatCard({
    label,
    value,
    variant = 'default',
    icon: IconComponent,
    to,
    onClick,
    children,
    'data-testid': testId,
}: StatCardProps) {
    const navigate = useNavigate()
    const isClickable = !!to || !!onClick

    const handleClick = () => {
        if (to) navigate(to)
        else if (onClick) onClick()
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleClick()
        }
    }

    const content = (
        <>
            <MutedText size="sm">{label}</MutedText>
            <StatValue value={value} variant={variant} data-testid={testId ? `${testId}-value` : undefined} />
            {children}
        </>
    )

    return (
        <Card
            className={cn(isClickable && 'hover:bg-muted/50 transition-colors cursor-pointer')}
            onClick={isClickable ? handleClick : undefined}
            role={isClickable ? 'link' : undefined}
            tabIndex={isClickable ? 0 : undefined}
            onKeyDown={isClickable ? handleKeyDown : undefined}
            data-testid={testId}
        >
            <CardContent className={cn('pt-6', IconComponent && 'flex items-center gap-4')}>
                {IconComponent && <IconComponent className="h-8 w-8 text-muted-foreground shrink-0" />}
                <div>{content}</div>
            </CardContent>
        </Card>
    )
}
